import React, { useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Modal, Badge, ListGroup, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../common/FalconCardHeader';
import AdvanceTable from '../common/advance-table/AdvanceTable';
import AdvanceTablePagination from '../common/advance-table/AdvanceTablePagination';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { createColumnHelper } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import { roleService } from '../../services/roleService';
import { useAuth } from '../../contexts/AuthContext';

const RoleManagement = () => {
  const { hasRole } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [selectedRoleForMenus, setSelectedRoleForMenus] = useState(null);
  const [allMenus, setAllMenus] = useState([]);
  const [roleMenus, setRoleMenus] = useState([]);
  const [updatingMenus, setUpdatingMenus] = useState(false);
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  const [copyFromRole, setCopyFromRole] = useState('');
  const [showCopyOptions, setShowCopyOptions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });


  // Cargar datos iniciales - versión robusta anti-duplicación
  useEffect(() => {
    let isMounted = true;
    let hasStartedLoading = false;

    const loadData = async () => {
      // Doble verificación para evitar carreras
      if (hasStartedLoading || !isMounted) {
        return;
      }

      hasStartedLoading = true;

      try {
        if (isMounted) setLoading(true);
        
        const rolesData = await roleService.getAll();
        
        if (isMounted) {
          // Asegurar que rolesData es un array - Los datos vienen en la propiedad 'data'
          const rolesArray = rolesData?.data || rolesData;
          const roles = Array.isArray(rolesArray) ? rolesArray : [];
          setRoles(roles);
        }
      } catch (error) {
        if (isMounted) {
          toast.error('Error al cargar los roles');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []); // Sin dependencias para ejecutar solo una vez

  const getVariantForRole = (roleName) => {
    switch (roleName?.toLowerCase()) {
      case 'admin': return 'danger';
      case 'manager': return 'info';
      case 'user': return 'warning';
      case 'viewer': return 'secondary';
      default: return 'primary';
    }
  };

  const columnHelper = useMemo(() => createColumnHelper(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Rol',
        cell: ({ row }) => {
          const systemRoles = ['admin', 'super_admin', 'user', 'manager', 'viewer'];
          const isSystemRole = systemRoles.includes(row.original.name.toLowerCase());
          
          return (
            <div className="d-flex align-items-center">
              <Badge bg={getVariantForRole(row.original.name)} className="me-2">
                <FontAwesomeIcon icon="user" />
              </Badge>
              <div>
                <h6 className="mb-0 fw-semibold">
                  {row.original.name}
                  {isSystemRole && (
                    <Badge bg="light" text="dark" className="ms-2 fs-11">
                      <FontAwesomeIcon icon="user-shield" className="me-1" />
                      Sistema
                    </Badge>
                  )}
                </h6>
                <small className="text-muted">{row.original.description}</small>
              </div>
            </div>
          );
        }
      }),
      columnHelper.accessor('users', {
        header: 'Usuarios',
        cell: ({ getValue }) => {
          const users = getValue() || [];
          return (
            <div className="text-center">
              <Badge bg="light" text="dark" className="fs-10">
                <FontAwesomeIcon icon="users" className="me-1" />
                {users.length}
              </Badge>
            </div>
          );
        },
        meta: { headerProps: { className: 'text-center' } }
      }),
      columnHelper.accessor('actions', {
        header: 'Acciones',
        cell: ({ row }) => {
          const systemRoles = ['admin', 'super_admin', 'user', 'manager', 'viewer'];
          const isSystemRole = systemRoles.includes(row.original.name.toLowerCase());
          
          return (
            <div className="text-center">
              <Button
                variant="falcon-info"
                size="sm"
                className="me-1"
                onClick={() => handleManageMenus(row.original)}
                title="Gestionar Menús"
              >
                <FontAwesomeIcon icon="list" />
              </Button>
              {isSystemRole ? (
                <Badge bg="warning" className="fs-11">
                  <FontAwesomeIcon icon="user-shield" className="me-1" />
                  Protegido
                </Badge>
              ) : (
                <>
                  <Button
                    variant="falcon-default"
                    size="sm"
                    className="me-1"
                    onClick={() => handleEdit(row.original)}
                    title="Editar rol"
                  >
                    <FontAwesomeIcon icon="edit" />
                  </Button>
                  <Button
                    variant="falcon-default"
                    size="sm"
                    className="text-danger"
                    onClick={() => handleDelete(row.original)}
                    title="Eliminar rol"
                  >
                    <FontAwesomeIcon icon="trash" />
                  </Button>
                </>
              )}
            </div>
          );
        },
        meta: { headerProps: { className: 'text-center' } }
      })
    ],
    [columnHelper]
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación de nombres del sistema
    const systemRoles = ['admin', 'super_admin', 'user', 'manager', 'viewer'];
    if (systemRoles.includes(formData.name.toLowerCase())) {
      toast.error('No se pueden usar nombres reservados del sistema para roles personalizados', {
        autoClose: 5000,
        icon: <FontAwesomeIcon icon="user-shield" />
      });
      return;
    }

    // Validación básica
    if (!formData.name.trim()) {
      toast.error('El nombre del rol es obligatorio');
      return;
    }

    if (formData.name.length < 3) {
      toast.error('El nombre del rol debe tener al menos 3 caracteres');
      return;
    }
    
    try {
      setSaving(true);
      
      if (editingRole) {
        await roleService.update(editingRole.id, formData);
        toast.success('Rol actualizado exitosamente');
      } else {
        await roleService.create(formData);
        toast.success('Rol creado exitosamente');
      }

      // Actualizar la lista de roles
      setRefreshing(true);
      const rolesData = await roleService.getAll();
      const roles = Array.isArray(rolesData) ? rolesData : [];
      setRoles(roles);
      
      resetForm();
      setShowRoleModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar el rol');
    } finally {
      setSaving(false);
      setRefreshing(false);
    }
  };

  const handleEdit = (role) => {
    // Verificar si es un rol del sistema y bloquearlo
    const systemRoles = ['admin', 'super_admin', 'user', 'manager', 'viewer'];
    const isSystemRole = systemRoles.includes(role.name.toLowerCase());
    
    if (isSystemRole) {
      toast.error('Los roles del sistema no pueden ser editados por seguridad', {
        autoClose: 5000,
        icon: <FontAwesomeIcon icon="user-shield" />
      });
      return;
    }

    setFormData({
      name: role.name,
      description: role.description || ''
    });
    setEditingRole(role);
    setShowRoleModal(true);
  };

  const handleDelete = (role) => {
    // Verificar si es un rol del sistema y bloquearlo
    const systemRoles = ['admin', 'super_admin', 'user', 'manager', 'viewer'];
    const isSystemRole = systemRoles.includes(role.name.toLowerCase());
    
    if (isSystemRole) {
      toast.error('Los roles del sistema no pueden ser eliminados por seguridad', {
        autoClose: 5000,
        icon: <FontAwesomeIcon icon="user-shield" />
      });
      return;
    }

    if (role.users && role.users.length > 0) {
      toast.warning('No se puede eliminar un rol que tiene usuarios asignados');
      return;
    }
    
    setRoleToDelete(role);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      
      await roleService.delete(roleToDelete.id);
      toast.success('Rol eliminado exitosamente');
      
      // Actualizar la lista de roles
      setRefreshing(true);
      const rolesData = await roleService.getAll();
      const roles = Array.isArray(rolesData) ? rolesData : [];
      setRoles(roles);
      
      setShowDeleteModal(false);
      setRoleToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al eliminar el rol');
    } finally {
      setDeleting(false);
      setRefreshing(false);
    }
  };

  const handleManageMenus = async (role) => {
    setSelectedRoleForMenus(role);
    
    try {
      // Cargar todos los menús y los menús del rol en paralelo
      const [allMenusResponse, roleMenusResponse] = await Promise.all([
        roleService.getAllMenus(),
        roleService.getRoleMenus(role.id)
      ]);

      if (allMenusResponse.success) {
        setAllMenus(allMenusResponse.data || []);
      }

      if (roleMenusResponse.success) {
        setRoleMenus(roleMenusResponse.data || []);
      }

      setShowMenuModal(true);
    } catch (error) {
      toast.error('Error al cargar los menús');
      console.error('Error loading menus:', error);
    }
  };

  const handleMenuToggle = (menuId) => {
    setRoleMenus(prev => {
      const menuExists = prev.some(menu => menu.id === menuId);
      
      if (menuExists) {
        // Remover menú
        return prev.filter(menu => menu.id !== menuId);
      } else {
        // Agregar menú
        const menuToAdd = allMenus.find(menu => menu.id === menuId);
        return menuToAdd ? [...prev, menuToAdd] : prev;
      }
    });
  };

  const handleSaveMenus = async () => {
    if (!selectedRoleForMenus) return;

    try {
      setUpdatingMenus(true);
      
      const menuIds = roleMenus.map(menu => menu.id);
      await roleService.updateRoleMenus(selectedRoleForMenus.id, menuIds);
      
      toast.success('Menús del rol actualizados exitosamente');
      setShowMenuModal(false);
      setSelectedRoleForMenus(null);
      setRoleMenus([]);
      setAllMenus([]);
      setMenuSearchTerm('');
      setCopyFromRole('');
      setShowCopyOptions(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al actualizar menús del rol');
    } finally {
      setUpdatingMenus(false);
    }
  };

  // 🆕 Copiar menús desde otro rol
  const handleCopyFromRole = async () => {
    if (!copyFromRole || !selectedRoleForMenus) return;

    try {
      const sourceRoleMenusResponse = await roleService.getRoleMenus(copyFromRole);
      if (sourceRoleMenusResponse.success) {
        setRoleMenus(sourceRoleMenusResponse.data || []);
        toast.success('Menús copiados exitosamente');
        setShowCopyOptions(false);
        setCopyFromRole('');
      }
    } catch (error) {
      toast.error('Error al copiar menús del rol');
    }
  };

  // 🆕 Seleccionar todos los menús
  const handleSelectAllMenus = () => {
    const filteredMenus = getFilteredMenus();
    setRoleMenus(filteredMenus);
    toast.info('Todos los menús seleccionados');
  };

  // 🆕 Limpiar todos los menús
  const handleClearAllMenus = () => {
    setRoleMenus([]);
    toast.info('Todos los menús deseleccionados');
  };

  // 🆕 Obtener menús filtrados por búsqueda
  const getFilteredMenus = () => {
    if (!menuSearchTerm.trim()) return allMenus;
    
    return allMenus.filter(menu => 
      menu.name.toLowerCase().includes(menuSearchTerm.toLowerCase()) ||
      (menu.path && menu.path.toLowerCase().includes(menuSearchTerm.toLowerCase()))
    );
  };

  // 🆕 Obtener estadísticas de menús
  const getMenuStatistics = () => {
    const total = allMenus.length;
    const selected = roleMenus.length;
    const percentage = total > 0 ? Math.round((selected / total) * 100) : 0;
    const parentMenus = roleMenus.filter(menu => !menu.parent_id).length;
    const childMenus = roleMenus.filter(menu => menu.parent_id).length;

    return { total, selected, percentage, parentMenus, childMenus };
  };

  const renderMenuTree = (menus, parentId = null, level = 0) => {
    const filteredMenus = menuSearchTerm.trim() ? getFilteredMenus() : menus;
    
    return filteredMenus
      .filter(menu => (menu.parent_id || null) === parentId)
      .sort((a, b) => a.order - b.order)
      .map(menu => {
        const isSelected = roleMenus.some(roleMenu => roleMenu.id === menu.id);
        const hasChildren = menus.some(m => m.parent_id === menu.id);
        const isHighlighted = menuSearchTerm.trim() && 
          (menu.name.toLowerCase().includes(menuSearchTerm.toLowerCase()) ||
           (menu.path && menu.path.toLowerCase().includes(menuSearchTerm.toLowerCase())));
        
        return (
          <div key={menu.id}>
            <ListGroup.Item 
              className={`border-0 py-2 ${isHighlighted ? 'bg-light' : ''}`}
              style={{ paddingLeft: `${level * 20 + 10}px` }}
            >
              <Form.Check
                type="checkbox"
                id={`menu-${menu.id}`}
                checked={isSelected}
                onChange={() => handleMenuToggle(menu.id)}
                label={
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      {menu.icon && (
                        <FontAwesomeIcon 
                          icon={menu.icon} 
                          className="me-2 text-muted" 
                          style={{ fontSize: '0.875rem' }}
                        />
                      )}
                      <span className={hasChildren ? 'fw-semibold' : ''}>
                        {menu.name}
                      </span>
                      {menu.path && (
                        <Badge bg="light" text="dark" className="ms-2 fs-11">
                          {menu.path}
                        </Badge>
                      )}
                    </div>
                    {isHighlighted && (
                      <Badge bg="warning" className="fs-11">
                        <FontAwesomeIcon icon="search" />
                      </Badge>
                    )}
                  </div>
                }
              />
            </ListGroup.Item>
            {!menuSearchTerm.trim() && renderMenuTree(menus, menu.id, level + 1)}
          </div>
        );
      });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
    setEditingRole(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowRoleModal(true);
  };

  // Configurar la tabla usando el hook existente
  const table = useAdvanceTable({
    data: roles,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  if (loading) {
    return (
      <Container fluid className="px-3">
        <div className="text-center py-5">
          <FontAwesomeIcon icon="spinner" spin size="2x" className="text-primary" />
          <p className="mt-3">Cargando roles...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4 card card-body">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-2">
                <FontAwesomeIcon icon="user-shield" className="me-2 text-primary" />
                Gestión de Roles
              </h2>
              <h5 className="text-700 fw-semi-bold">
                Administra roles y permisos del sistema
              </h5>
            </Col>
            <Col xs="auto">
              <Button 
                variant="primary" 
                onClick={openCreateModal}
              >
                <FontAwesomeIcon icon="plus" className="me-1" />
                Nuevo Rol
              </Button>
            </Col>
          </Row>
        </div>

        {/* Estadísticas */}
        <Row className="g-3 mb-4">
          <Col lg={3} md={6}>
            <Card className="bg-primary text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{Array.isArray(roles) ? roles.length : 0}</h4>
                    <small>Total Roles</small>
                  </div>
                  <FontAwesomeIcon icon="user-shield" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-success text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{Array.isArray(roles) ? roles.filter(r => !['admin', 'user', 'manager', 'viewer'].includes(r.name)).length : 0}</h4>
                    <small>Roles Personalizados</small>
                  </div>
                  <FontAwesomeIcon icon="cogs" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-info text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{Array.isArray(roles) ? roles.reduce((sum, r) => sum + (r.users?.length || 0), 0) : 0}</h4>
                    <small>Usuarios Asignados</small>
                  </div>
                  <FontAwesomeIcon icon="users" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-info text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{Array.isArray(roles) ? roles.length : 0}</h4>
                    <small>Total Roles</small>
                  </div>
                  <FontAwesomeIcon icon="lock" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Roles */}
        <Card className="shadow-sm">
          <FalconCardHeader
            title="Lista de Roles"
            endEl={
              <div className="d-flex gap-2">
                <Form.Control
                  type="search"
                  placeholder="Buscar roles..."
                  className="form-control-sm"
                  style={{ width: '200px' }}
                />
                <Button variant="falcon-default" size="sm">
                  <FontAwesomeIcon icon="filter" />
                </Button>
              </div>
            }
          />
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                headerClassName="bg-light text-900"
                bodyClassName="bg-white"
                tableProps={{
                  size: 'sm',
                  className: 'fs-10 mb-0'
                }}
              />
              <div className="px-3 pb-3">
                <AdvanceTablePagination />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>

        {/* Modal de Rol */}
        <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={editingRole ? "edit" : "plus"} className="me-2 text-primary" />
              {editingRole ? 'Editar Rol' : 'Crear Nuevo Rol'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="user" className="me-1 text-primary" />
                      Nombre del Rol *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      placeholder="Ej: Editor, Manager, etc."
                      disabled={editingRole && ['admin', 'user', 'manager', 'viewer'].includes(editingRole.name)}
                    />
                    {editingRole && ['admin', 'user', 'manager', 'viewer'].includes(editingRole.name) && (
                      <Form.Text className="text-muted">
                        Los roles del sistema no pueden cambiar de nombre
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon="file-alt" className="me-1 text-success" />
                      Descripción *
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      required
                      placeholder="Describe las responsabilidades y alcance de este rol..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowRoleModal(false)}
                disabled={saving}
              >
                <FontAwesomeIcon icon="times" className="me-1" />
                Cancelar
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Guardando...</span>
                    </div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={editingRole ? "check" : "plus"} className="me-1" />
                    {editingRole ? 'Actualizar Rol' : 'Crear Rol'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal de Confirmación de Eliminación */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2 text-warning" />
              Confirmar Eliminación
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Estás seguro de que deseas eliminar este rol?</p>
            {roleToDelete && (
              <div className="bg-light p-3 rounded">
                <strong>Rol:</strong> {roleToDelete.name}<br/>
                <strong>Descripción:</strong> {roleToDelete.description}<br/>
                <strong>Usuarios asignados:</strong> {roleToDelete.users?.length || 0}
              </div>
            )}
            <p className="text-danger mt-3 mb-0">
              <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
              Esta acción no se puede deshacer.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              <FontAwesomeIcon icon="times" className="me-1" />
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Eliminando...</span>
                  </div>
                  Eliminando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="trash" className="me-1" />
                  Eliminar Rol
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal para gestionar menús del rol */}
        <Modal 
          show={showMenuModal} 
          onHide={() => setShowMenuModal(false)}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon="list" className="text-info me-2" />
              Gestionar Menús - {selectedRoleForMenus?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            {selectedRoleForMenus && (
              <>
                <Alert variant="info" className="d-flex align-items-center">
                  <FontAwesomeIcon icon="sitemap" className="me-2" />
                  <div>
                    <strong>Configurando menús para:</strong> {selectedRoleForMenus.name}
                    <br />
                    <small>
                      Selecciona los menús que estarán disponibles para usuarios con este rol.
                    </small>
                  </div>
                </Alert>

                {/* 🆕 Estadísticas y controles */}
                <Row className="mb-3">
                  <Col md={12}>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleSelectAllMenus}
                        disabled={updatingMenus}
                        title="Seleccionar todos los menús"
                      >
                        <FontAwesomeIcon icon="check-double" className="me-1" />
                        Todo
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleClearAllMenus}
                        disabled={updatingMenus}
                        title="Deseleccionar todos los menús"
                      >
                        <FontAwesomeIcon icon="times" className="me-1" />
                        Limpiar
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => setShowCopyOptions(!showCopyOptions)}
                        disabled={updatingMenus}
                        title="Copiar desde otro rol"
                      >
                        <FontAwesomeIcon icon="copy" className="me-1" />
                        Copiar
                      </Button>
                    </div>
                  </Col>
                </Row>

                {/* 🆕 Opciones de copia */}
                {showCopyOptions && (
                  <Alert variant="light" className="border">
                    <Row className="align-items-end">
                      <Col md={8}>
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon="copy" className="me-1" />
                          Copiar configuración desde otro rol:
                        </Form.Label>
                        <Form.Select
                          value={copyFromRole}
                          onChange={(e) => setCopyFromRole(e.target.value)}
                          disabled={updatingMenus}
                        >
                          <option value="">Seleccionar rol origen...</option>
                          {roles
                            .filter(role => role.id !== selectedRoleForMenus.id)
                            .map(role => (
                              <option key={role.id} value={role.id}>
                                {role.name} - {role.description}
                              </option>
                            ))}
                        </Form.Select>
                      </Col>
                      <Col md={4}>
                        <div className="d-flex gap-2">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleCopyFromRole}
                            disabled={!copyFromRole || updatingMenus}
                          >
                            <FontAwesomeIcon icon="copy" className="me-1" />
                            Copiar
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowCopyOptions(false)}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Alert>
                )}

                {/* 🆕 Barra de búsqueda */}
                <Row className="mb-3">
                  <Col>
                    <Form.Control
                      type="search"
                      placeholder="🔍 Buscar menús por nombre o ruta..."
                      value={menuSearchTerm}
                      onChange={(e) => setMenuSearchTerm(e.target.value)}
                      className="border-primary"
                    />
                    {menuSearchTerm.trim() && (
                      <Form.Text className="text-muted">
                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                        Mostrando resultados para: <strong>"{menuSearchTerm}"</strong>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 ms-2"
                          onClick={() => setMenuSearchTerm('')}
                        >
                          Limpiar
                        </Button>
                      </Form.Text>
                    )}
                  </Col>
                </Row>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <FontAwesomeIcon icon="sitemap" className="me-2 text-muted" />
                    Estructura de Menús
                    {menuSearchTerm.trim() && (
                      <Badge bg="warning" className="ms-2">
                        Búsqueda activa
                      </Badge>
                    )}
                  </h6>
                  <Badge bg="secondary">
                    {roleMenus.length} de {allMenus.length} seleccionados
                  </Badge>
                </div>

                <ListGroup className="border rounded">
                  {allMenus.length > 0 ? (
                    <>
                      {renderMenuTree(allMenus)}
                      {menuSearchTerm.trim() && getFilteredMenus().length === 0 && (
                        <ListGroup.Item className="text-center text-muted py-4">
                          <FontAwesomeIcon icon="search" className="me-2" />
                          No se encontraron menús que coincidan con "{menuSearchTerm}"
                        </ListGroup.Item>
                      )}
                    </>
                  ) : (
                    <ListGroup.Item className="text-center text-muted py-4">
                      <FontAwesomeIcon icon="spinner" spin className="me-2" />
                      Cargando menús...
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowMenuModal(false)}
              disabled={updatingMenus}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveMenus}
              disabled={updatingMenus}
            >
              {updatingMenus ? (
                <>
                  <FontAwesomeIcon icon="spinner" spin className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="save" className="me-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default RoleManagement; 